import cx from 'classnames'
import {
  ChangeEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef
} from 'react'
import { noop } from 'rxjs'
import styles from './index.module.scss'

export interface Props {
  onChange?: (e: React.ChangeEvent) => void
  onClick?: (e: React.MouseEvent) => void
  locator: string
  checked?: boolean
  indeterminate?: boolean
  disabled?: boolean
  suppressDisabledStyling?: boolean
  checkboxClass?: string
}

const Checkbox = ({
  children,
  locator,
  onChange = noop,
  checked,
  indeterminate = false,
  disabled = false,
  suppressDisabledStyling = false,
  checkboxClass = '',
  onClick = noop
}: PropsWithChildren<Props>) => {
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    // There is no way to define the “indeterminate” attribute, we have to
    // use a ref for that.
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange(event)
      }
    },
    [disabled, onChange]
  )

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      if (!disabled) {
        onClick(event)
      }
    },
    [disabled, onClick]
  )

  return (
    <div
      className={cx(
        styles.wrapper,
        disabled && !suppressDisabledStyling && styles.disabled
      )}
    >
      <label className={styles.label} data-testid={locator}>
        <span className={cx(styles.boxContainer, checkboxClass)}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={checked}
            onChange={handleChange}
            onClick={handleClick}
            ref={inputRef}
            disabled={disabled}
          />
        </span>
        {!!children && <span className={styles.text}>{children}</span>}
      </label>
    </div>
  )
}

export default Checkbox
